/**
 * Enveloppe applicative, disponibilité hors ligne et mise à jour —
 * EF-PLT-003, EF-PLT-004.
 */

export type OfflineStatus = 'préparation en cours' | 'disponible hors ligne' | 'hors ligne indisponible';

export interface EnvelopeReport {
  readonly status: OfflineStatus;
  /** Ressources absentes ou non vérifiées, nommées de manière diagnosticable. */
  readonly missing: readonly string[];
  readonly buildVersion: string | null;
}

export interface UpdateState {
  /** Vrai lorsqu'une nouvelle version est installée et attend une décision. */
  readonly waiting: boolean;
  readonly checking: boolean;
  /** Message d'échec du dernier contrôle de bon fonctionnement. */
  readonly failure: string | null;
}

type Listener = (report: EnvelopeReport) => void;
type UpdateListener = (state: UpdateState) => void;

const INITIAL_REPORT: EnvelopeReport = {
  status: 'préparation en cours',
  missing: [],
  buildVersion: null,
};

/**
 * Enregistre le service worker et suit l'état de l'enveloppe.
 *
 * Le service worker ne s'active jamais de lui-même : une nouvelle version
 * s'installe, met en cache son enveloppe, puis attend. L'application détecte
 * cette version en attente et propose son activation.
 */
export class OfflineManager {
  private registration: ServiceWorkerRegistration | null = null;
  private report: EnvelopeReport = INITIAL_REPORT;
  private update: UpdateState = { waiting: false, checking: false, failure: null };
  private readonly listeners = new Set<Listener>();
  private readonly updateListeners = new Set<UpdateListener>();

  get current(): EnvelopeReport {
    return this.report;
  }

  get updateState(): UpdateState {
    return this.update;
  }

  static get isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  }

  onReport(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.report);
    return () => this.listeners.delete(listener);
  }

  onUpdate(listener: UpdateListener): () => void {
    this.updateListeners.add(listener);
    listener(this.update);
    return () => this.updateListeners.delete(listener);
  }

  private emit(report: EnvelopeReport): void {
    this.report = report;
    for (const listener of this.listeners) listener(report);
  }

  private emitUpdate(state: UpdateState): void {
    this.update = state;
    for (const listener of this.updateListeners) listener(state);
  }

  async register(scriptUrl = '/sw.js'): Promise<void> {
    if (!OfflineManager.isSupported) {
      this.emit({ status: 'hors ligne indisponible', missing: ['service worker'], buildVersion: null });
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event: MessageEvent<unknown>) => {
      this.handleMessage(event.data);
    });

    try {
      this.registration = await navigator.serviceWorker.register(scriptUrl, { scope: '/' });
    } catch (error) {
      this.emit({
        status: 'hors ligne indisponible',
        missing: [describe(error)],
        buildVersion: null,
      });
      return;
    }

    this.registration.addEventListener('updatefound', () => {
      const installing = this.registration?.installing;
      installing?.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
          this.emitUpdate({ ...this.update, waiting: true });
        }
        if (installing.state === 'installed') void this.verify();
      });
    });

    if (this.registration.waiting !== null) this.emitUpdate({ ...this.update, waiting: true });

    await navigator.serviceWorker.ready;
    await this.verify();
  }

  /** Demande la relecture effective de chaque entrée depuis le cache. */
  async verify(): Promise<void> {
    const worker = navigator.serviceWorker?.controller ?? this.registration?.active ?? null;
    if (worker === null) {
      this.emit(INITIAL_REPORT);
      return;
    }
    worker.postMessage({ type: 'VERIFY_ENVELOPE' });
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== 'object' || data === null) return;
    const message = data as Record<string, unknown>;

    if (message['type'] === 'ENVELOPE_STATUS') {
      const status = message['status'];
      const missing = Array.isArray(message['missing']) ? (message['missing'] as string[]) : [];
      const buildVersion = typeof message['buildVersion'] === 'string' ? message['buildVersion'] : null;
      this.emit({
        status: isOfflineStatus(status) ? status : 'hors ligne indisponible',
        missing,
        buildVersion,
      });
    }
  }

  /** Interroge l'origine HTTPS de l'application. N'émet aucune requête tierce. */
  async checkForUpdate(): Promise<void> {
    if (this.registration === null) return;
    this.emitUpdate({ ...this.update, checking: true });
    try {
      await this.registration.update();
      this.emitUpdate({
        waiting: this.registration.waiting !== null,
        checking: false,
        failure: null,
      });
    } catch (error) {
      this.emitUpdate({ ...this.update, checking: false, failure: describe(error) });
    }
  }

  /**
   * Séquence d'activation de EF-PLT-004, dans cet ordre, sans exception :
   * sauvegarde locale, prise de contrôle et rechargement, migration, contrôle
   * de bon fonctionnement.
   */
  async activateUpdate(saveFirst: () => Promise<void>): Promise<void> {
    const waiting = this.registration?.waiting;
    if (waiting === undefined || waiting === null) return;

    await saveFirst();

    await new Promise<void>((resolve) => {
      const onChange = (): void => {
        if (waiting.state === 'activated') {
          navigator.serviceWorker.removeEventListener('controllerchange', onChange);
          resolve();
        }
      };
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        resolve();
      });
      waiting.addEventListener('statechange', onChange);
      waiting.postMessage({ type: 'ACTIVATE_UPDATE' });
    });

    globalThis.location.reload();
  }

  /**
   * Confirme le bon fonctionnement après activation. Les caches antérieurs ne
   * sont supprimés qu'ici : c'est ce qui rend le retour en arrière possible
   * sans réseau.
   */
  confirmHealthy(): void {
    navigator.serviceWorker?.controller?.postMessage({ type: 'CONFIRM_HEALTHY' });
  }
}

function isOfflineStatus(value: unknown): value is OfflineStatus {
  return (
    value === 'préparation en cours' ||
    value === 'disponible hors ligne' ||
    value === 'hors ligne indisponible'
  );
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Vrai lorsque le navigateur se déclare hors ligne. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}
